import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Lock, KeyRound } from 'lucide-react';
import { authAPI } from '../api/auth';
import { useAuth } from '../context/AuthContext';

/**
 * Post-OTP-login one-time modal per EMAIL_OTP_AUTH_PRD §4.1.
 *
 * Fires once when the current user has `mustSetPasswordOnFirstLogin=true`
 * (set by the Phase 3 migration on the ~5 orphan users in production).
 * Offers: (a) set a password now — 12+ chars, letter + digit — so they
 * can skip typing an email code next time, OR (b) skip forever — keeps
 * them on OTP-only. Either action flips the DB flag to false so this
 * modal never renders again for that user.
 *
 * Non-invasive: mounts at MainLayout root, does not block any other flow.
 * If the endpoints 404 (AUTH_MODE=classic in an older deploy), the modal
 * simply doesn't open — no error surfaced to the user.
 */
const SetPasswordNudgeDialog = () => {
  const { user, updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  useEffect(() => {
    if (user?.mustSetPasswordOnFirstLogin && !dismissedThisSession) {
      // Small delay so the modal doesn't jump-scare a user mid-page-load.
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [user, dismissedThisSession]);

  const closeModal = () => {
    setOpen(false);
    setDismissedThisSession(true);
  };

  const parseError = (err, fallback) => {
    if (err?.message) return err.message;
    const d = err?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(e => e.msg || JSON.stringify(e)).join(', ');
    return fallback;
  };

  const clearLocalFlag = () => {
    if (typeof updateUser === 'function') {
      updateUser({ mustSetPasswordOnFirstLogin: false });
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    setError('');
    try {
      await authAPI.skipPasswordNudge();
      clearLocalFlag();
      closeModal();
    } catch (err) {
      // Even if the endpoint 404s (older backend), don't surface an error —
      // just dismiss for this session so the modal isn't annoying.
      clearLocalFlag();
      closeModal();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!password || password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
      setError('Password must contain both letters and digits.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await authAPI.setPassword(password);
      clearLocalFlag();
      closeModal();
    } catch (err) {
      setError(parseError(err, 'Could not set password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (!user?.mustSetPasswordOnFirstLogin) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) closeModal(); }}>
      <DialogContent className="sm:max-w-md" data-testid="set-password-nudge-dialog">
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
            <KeyRound className="h-6 w-6 text-orange-600" />
          </div>
          <DialogTitle className="text-center">Set a password?</DialogTitle>
          <DialogDescription className="text-center pt-1">
            You just signed in with an email code. Setting a password lets you skip that step next time —
            still safer than the code-only flow if you occasionally lose access to your inbox.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert className="bg-red-50 border-red-200">
            <AlertDescription className="text-red-800" data-testid="nudge-error">{error}</AlertDescription>
          </Alert>
        )}

        {showForm ? (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="nudge-form">
            <div>
              <Label htmlFor="nudge-password">New password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="nudge-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={12}
                  required
                  className="pl-10"
                  disabled={loading}
                  placeholder="At least 12 characters, letters + digits"
                  autoFocus
                  data-testid="nudge-password-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="nudge-confirm">Confirm password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="nudge-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={12}
                  required
                  className="pl-10"
                  disabled={loading}
                  data-testid="nudge-confirm-input"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={handleSkip}
                disabled={loading}
                data-testid="nudge-skip-btn"
              >
                Skip for now
              </Button>
              <Button
                type="submit"
                className="bg-[#F97316] hover:bg-[#F59E0B]"
                disabled={loading || !password || !confirm}
                data-testid="nudge-submit-btn"
              >
                {loading ? 'Saving…' : 'Set password'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              disabled={loading}
              data-testid="nudge-skip-btn"
            >
              No thanks — keep using codes
            </Button>
            <Button
              type="button"
              className="bg-[#F97316] hover:bg-[#F59E0B]"
              onClick={() => setShowForm(true)}
              disabled={loading}
              data-testid="nudge-start-btn"
            >
              Set a password
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SetPasswordNudgeDialog;
